import { useMemo } from "react"
import { makeStyles } from "tss-react/mui"
import { Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"

import { groupPriceChangesByReason, PriceChange, PriceChangeReason } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    list: {
        maxHeight: "16rem",
        overflowY: "auto",
        width: "100%",
    },
    sectionTitle: {
        color: theme.palette.tailwind.slate[500],
        textTransform: "uppercase",
        letterSpacing: ".05em",
    },
    sectionHint: {
        color: theme.palette.tailwind.slate[500],
    },
    row: {
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
        paddingTop: ".5rem",
    },
    productName: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    quantity: {
        color: theme.palette.tailwind.slate[500],
    },
    oldPrice: {
        color: theme.palette.tailwind.slate[500],
        textDecoration: "line-through",
        whiteSpace: "nowrap",
    },
    newPrice: {
        color: theme.palette.tailwind.slate[800],
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    unchangedPrice: {
        color: theme.palette.tailwind.slate[500],
        whiteSpace: "nowrap",
    },
    arrowIcon: {
        color: theme.palette.tailwind.slate[400],
    },
}))

type PriceChangesListProps = Readonly<{
    changes: ReadonlyArray<PriceChange>
    priceListName?: string
}>

export function PriceChangesList({ changes, priceListName }: PriceChangesListProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    const groups = useMemo(() => groupPriceChangesByReason(changes), [changes])

    if (groups.length === 0) return null

    return (
        <Stack gap="1.5rem" className={classes.list}>
            {groups.map(({ reason, items }) => (
                <Stack key={reason} gap=".5rem">
                    <Stack gap=".25rem">
                        {reason === PriceChangeReason.PRICE_CHANGED && (
                            <LocalizedStrict
                                id="create-order-order-summary-meeting-services-product-price-mismatch-dialog-section-price-changed"
                                vars={{ count: items.length }}
                            >
                                <Typography variant="caption" className={classes.sectionTitle}>
                                    Price changed ({items.length})
                                </Typography>
                            </LocalizedStrict>
                        )}
                        {reason === PriceChangeReason.MISSING_IN_PRICE_LIST && (
                            <LocalizedStrict
                                id="create-order-order-summary-meeting-services-product-price-mismatch-dialog-section-missing-product"
                                vars={{ count: items.length, priceList: priceListName ?? "" }}
                            >
                                <Typography variant="caption" className={classes.sectionTitle}>
                                    Not available in price list {priceListName} ({items.length})
                                </Typography>
                            </LocalizedStrict>
                        )}
                        {reason === PriceChangeReason.OTHER_PRICE_LIST && (
                            <LocalizedStrict
                                id="create-order-order-summary-meeting-services-product-price-mismatch-dialog-section-other-price-list"
                                vars={{ count: items.length }}
                            >
                                <Typography variant="caption" className={classes.sectionTitle}>
                                    From another price list ({items.length})
                                </Typography>
                            </LocalizedStrict>
                        )}
                        {reason !== PriceChangeReason.PRICE_CHANGED && (
                            <LocalizedStrict id="create-order-order-summary-meeting-services-product-price-mismatch-dialog-section-unchanged-hint">
                                <Typography variant="caption" className={classes.sectionHint}>
                                    The price of these items stays as it is in the order.
                                </Typography>
                            </LocalizedStrict>
                        )}
                    </Stack>
                    {items.map((item) => (
                        <Stack
                            key={`${item.id}-${item.orderPrice.amount}-${item.orderPrice.scale}`}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap="1rem"
                            className={classes.row}
                        >
                            <Stack alignItems="flex-start" minWidth={0}>
                                <Typography variant="body2" className={classes.productName}>
                                    {item.name}
                                </Typography>
                                <LocalizedStrict
                                    id={
                                        item.quantity > 1
                                            ? "create-order-order-summary-meeting-services-product-items"
                                            : "create-order-order-summary-meeting-services-product-item"
                                    }
                                    vars={{ count: item.quantity }}
                                >
                                    <Typography variant="caption" className={classes.quantity}>
                                        {item.quantity} {item.quantity > 1 ? "items" : "item"}
                                    </Typography>
                                </LocalizedStrict>
                            </Stack>
                            {item.catalogPrice ? (
                                <Stack direction="row" alignItems="center" gap=".5rem">
                                    <Typography variant="body2" className={classes.oldPrice}>
                                        {moneyFactory.format(toDinero(item.orderPrice))}
                                    </Typography>
                                    <FontAwesomeIcon icon={faArrowRight} className={classes.arrowIcon} />
                                    <Typography variant="body2" className={classes.newPrice}>
                                        {moneyFactory.format(toDinero(item.catalogPrice))}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="body2" className={classes.unchangedPrice}>
                                    {moneyFactory.format(toDinero(item.orderPrice))}
                                </Typography>
                            )}
                        </Stack>
                    ))}
                </Stack>
            ))}
        </Stack>
    )
}
